import TextField from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormik } from "formik";
import React, { useContext, useMemo, useState, useEffect } from "react";
import SectionHeader from "../../../../util/component/SectionHeader";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useTenant from "../../hooks/useTenant";
import TransferReasonPage from "../../page/TransferReasonPage";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import GenericFilterBox from "../common/GenericFilterBox";
import { nameof } from "../common/ObjectUtils";
import AddEditTransferReasonPage from "./AddEditTransferReasonPage";
import { TransferContext } from "./TransferContext";
import TransferReasonService from "./TransferReasonService";
import TransferReasonTable from "./TransferReasonTable";
import { TransferReasonTypes } from "./Types";
import EnumChangeName from "../common/EnumChangeName";

interface Props {
  formik: any;
}

const SearchModal = (props: Props) => {
  const context = useContext(TransferContext);
  const { tenant, userLocations, allLocations } = useContext(ContextOperation);
  const { tenantOptions: options } = useTenant();
  const t = useOperationTranslation();

  const enumName = EnumChangeName();

  const [addPage, setPage] = useState<boolean>(false);
  const [statusNumber, setStatusNumber] = useState<any>({});

  const [initialFormikValue, setInitialFormikValue] =
    useState<TransferReasonTypes.AddFunctionTypes>({
      id: "",
      transferReasonCode: "",
      status: null,
      startStationId: "",
      startStationType: "",
      startDate: null,
      expectedEndStationId: "",
      expectedEndStationType: "",
      expectedEndDate: null,
      endStationId: "",
      endStationType: "",
      endDate: null,
      note: "",
      operatorCode: "",
      companyName: "",
      companyCity: "",
      plates: [],
      driversId: [],
      transferType: "",
      shopRepairLms: ""
    });
  const [datasourceAcriss, totalElementsAcriss, updateDatasourceAcriss] =
    useAgGridCustomDatasource(
      TransferReasonService.get.filterTransferReason,
      [{
        tenant,
        ...props.formik.values,
        plate: props.formik.values.plate?.trim(),
        vin: props.formik.values.vin?.trim(),
        number: props.formik.values.number?.trim(),
        startStationId: allLocations.find(el => el.key === props.formik.values.startStationId)?.bpCode,
        startStationType: allLocations.find(el => el.key === props.formik.values.startStationId)?.type,
        expectedEndStationId: allLocations.find(el => el.key === props.formik.values.expectedEndStationId)?.bpCode,
        expectedEndStationType: allLocations.find(el => el.key === props.formik.values.expectedEndStationId)?.type,
        endStationId: allLocations.find(el => el.key === props.formik.values.endStationId)?.bpCode,
        endStationType: allLocations.find(el => el.key === props.formik.values.endStationId)?.type,
      }],
      [tenant, props.formik.submitCount]
    );

  useEffect(() => {
    TransferReasonService.get
      .statusCounter(
        tenant,
        props.formik.values,
        allLocations.find(el => el.key === props.formik.values.startStationId)?.bpCode,
        allLocations.find(el => el.key === props.formik.values.startStationId)?.type,
        allLocations.find(el => el.key === props.formik.values.expectedEndStationId)?.bpCode,
        allLocations.find(el => el.key === props.formik.values.expectedEndStationId)?.type,
        allLocations.find(el => el.key === props.formik.values.endStationId)?.bpCode,
        allLocations.find(el => el.key === props.formik.values.endStationId)?.type,
      )
      .then((response) => setStatusNumber(response));
  }, [datasourceAcriss]);


  const isDisabled = () => {
    if (Object.keys(props.formik?.values).length !== 0 || options.length > 1) {
      return Object.values(props.formik?.values).every(
        (x) => x === null || x === ""
      );
    }
    return true;
  };

  const statusTypeArray: AutoCompleteOption[] = [
    {
      value: "APPROVED",
      label: t("transferReason.autocomplete.approved"),
    },
    {
      label: t("transferReason.autocomplete.rejected"),
      value: "REJECTED",
    },
    {
      label: t("transferReason.autocomplete.toBeApproved"),
      value: "TO_BE_APPROVED",
    },
    {
      label: t("transferReason.autocomplete.created"),
      value: "CREATED",
    },
    {
      label: t("transferReason.autocomplete.approvedWithDdt"),
      value: "APPROVED_WITH_DDT",
    },
    {
      label: t("transferReason.autocomplete.transferCompleted"),
      value: "TRANSFER_COMPLETED",
    },
    {
      label: t("transferReason.autocomplete.duplicateCancelledBySystem"),
      value: "DUPLICATE_CANCELLED_BY_SYSTEM",
    },
  ];

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return (
      userLocations.map((loc) => ({
        label: loc.descr || "",
        value: loc.key || "",
      })) || []
    );
  }, [userLocations]);

  const allLocs = useMemo<AutoCompleteOption[]>(() => allLocations.map(loc => ({
    label: `${loc.locationEntityDesc} (${enumName.locationType(loc.type).toUpperCase()})` || "",
    value: loc.key || "",
  })), [allLocations]);

  return (
    <React.Fragment>
      {!addPage ? (
        <>
          <SectionHeader
            title={
              t("transferReason.titleFirst") +
              " " +
              t("transferReason.titleSecond")
            }
          />
          <form id="transfer-reason-form" onSubmit={props.formik.handleSubmit}>
            <GenericFilterBox
              showExtrasFilters={true}
              onReset={props.formik.resetForm}
              formId="transfer-reason-form"
              disableResetButton={isDisabled()}
            >
              <TextField
                name={nameof<TransferReasonTypes.SearchFunctionTypes>("plate")}
                label={t("transferReason.search.plate")}
                value={props.formik.values.plate || ""}
                onChange={props.formik.handleChange}
                fullWidth={true}
                inputProps={{
                  pattern: "[^%*$)(+&?^~Â`£=!@'_§#-]{0,}",
                  onInput: (event) => {
                    if (event.currentTarget.validity.patternMismatch) {
                      event.currentTarget.setCustomValidity(
                        t("vehicle.filterModal.regexMessage")
                      );
                    } else {
                      event.currentTarget.setCustomValidity("");
                    }
                  },
                }}
              />

              <TextField
                name={nameof<TransferReasonTypes.SearchFunctionTypes>("vin")}
                label={t("transferReason.search.vin")}
                value={props.formik.values.vin || ""}
                onChange={props.formik.handleChange}
                fullWidth={true}
                inputProps={{
                  pattern: "[^%*$)(+&?^~Â`£=!@'_§#-]{0,}",
                  onInput: (event) => {
                    if (event.currentTarget.validity.patternMismatch) {
                      event.currentTarget.setCustomValidity(
                        t("vehicle.filterModal.regexMessage")
                      );
                    } else {
                      event.currentTarget.setCustomValidity("");
                    }
                  },
                }}
              />

              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "requestDateFrom"
                    ),
                    dayjs(e).isValid()
                      ? dayjs(e).isValid()
                        ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                        : ""
                      : ""
                  );
                }}
                value={props.formik.values.requestDateFrom || null}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.requestDate")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="requestDateFrom"
                    />
                  </>
                )}
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "requestDateTo"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                value={props.formik.values.requestDateTo || null}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.requestDateTo")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="requestDateTo"
                    />
                  </>
                )}
              />
              <AutoComplete
                fullWidth
                label={t("transferReason.search.reason")}
                value={props.formik.values.transferReasonCode}
                options={context.reason}
                onChange={(value) =>
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "transferReasonCode"
                    ),
                    value
                  )
                }
              />
              <TextField
                name={nameof<TransferReasonTypes.SearchFunctionTypes>("number")}
                label={t("transferReason.search.requestNumber")}
                value={props.formik.values.number || ""}
                onChange={props.formik.handleChange}
                fullWidth={true}
                inputProps={{
                  pattern: "[^%*$)(+&?^~Â`£=!@'_§#-]{0,}",
                  onInput: (event) => {
                    if (event.currentTarget.validity.patternMismatch) {
                      event.currentTarget.setCustomValidity(
                        t("vehicle.filterModal.regexMessage")
                      );
                    } else {
                      event.currentTarget.setCustomValidity("");
                    }
                  },
                }}
              />
              <AutoComplete
                fullWidth
                label={t("transferReason.search.status")}
                value={props.formik.values.state || ""}
                options={statusTypeArray}
                onChange={(value) =>
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>("state"),
                    value
                  )
                }
              />
              <AutoComplete
                fullWidth
                label={t("transferReason.search.startStation")}
                value={props.formik.values.startStationId}
                options={allLocs}
                onChange={(value) =>
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "startStationId"
                    ),
                    value
                  )
                }
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "startDateFrom"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                value={props.formik.values.startDateFrom || null}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.leavingDate")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="startDateFrom"
                    />
                  </>
                )}
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "startDateTo"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                value={props.formik.values.startDateTo || null}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.startDateTo")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="startDateTo"
                    />
                  </>
                )}
              />
              <AutoComplete
                fullWidth
                label={t("transferReason.search.predictedArrivalStation")}
                value={props.formik.values.expectedEndStationId}
                options={allLocs}
                onChange={(value) =>
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "expectedEndStationId"
                    ),
                    value
                  )
                }
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "expectedEndDateFrom"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.predictedArrivalDate")}
                value={props.formik.values.expectedEndDateFrom || null}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="expectedEndDateFrom"
                    />
                  </>
                )}
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "expectedEndDateTo"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.predictedArrivalDateTo")}
                value={props.formik.values.expectedEndDateTo || null}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="predictedArrivalDateTo"
                    />
                  </>
                )}
              />
              <AutoComplete
                fullWidth
                label={t("transferReason.search.actualArrivalStation")}
                value={props.formik.values.endStationId}
                options={allLocs}
                onChange={(value) =>
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "endStationId"
                    ),
                    value
                  )
                }
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "endDateFrom"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                value={props.formik.values.endDateFrom || null}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.actualArrivalDate")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="actualArrivalDate"
                    />
                  </>
                )}
              />
              <DateTimePicker
                onChange={(e: any) => {
                  props.formik.setFieldValue(
                    nameof<TransferReasonTypes.SearchFunctionTypes>(
                      "endDateTo"
                    ),
                    dayjs(e).isValid()
                      ? dayjs.utc(e).format("YYYY-MM-DDTHH:mm:ssZ")
                      : ""
                  );
                }}
                value={props.formik.values.endDateTo || null}
                mask={"__/__/____ __:__"}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transferReason.search.actualArrivalDateTo")}
                ampm={false}
                renderInput={(params) => (
                  <>
                    <TextField
                      variant="standard"
                      fullWidth
                      {...params}
                      name="actualArrivalDateTo"
                    />
                  </>
                )}
              />
              <TextField
                name={nameof<TransferReasonTypes.SearchFunctionTypes>(
                  "operatorCode"
                )}
                label={t("transferReason.search.operatorCode")}
                value={props.formik.values.operatorCode || ""}
                onChange={props.formik.handleChange}
                fullWidth={true}
                inputProps={{
                  pattern: "[^%*$)(\"+&?^~Â`£=!@'_/§#-]{0,}",
                  onInput: (event) => {
                    if (event.currentTarget.validity.patternMismatch) {
                      event.currentTarget.setCustomValidity(
                        t("vehicle.filterModal.regexMessage")
                      );
                    } else {
                      event.currentTarget.setCustomValidity("");
                    }
                  },
                }}
              />
            </GenericFilterBox>
          </form>
          <TransferReasonTable
            table={{
              totalElements: totalElementsAcriss,
              datasource: datasourceAcriss,
              tenant: tenant,
              values: props.formik.values,
              startStationId: allLocations.find(el => el.key === props.formik.values.startStationId)?.bpCode,
              startStationType: allLocations.find(el => el.key === props.formik.values.startStationId)?.type,
              startTimezoneId: props?.formik?.values?.startTimeZoneId,
              expectedEndStationId: allLocations.find(el => el.key === props.formik.values.expectedEndStationId)?.bpCode,
              expectedEndStationType: allLocations.find(el => el.key === props.formik.values.expectedEndStationId)?.type,
              expectedTimezoneId: props?.formik?.values?.expectedTimeZoneId,
              endStationId: allLocations.find(el => el.key === props.formik.values.endStationId)?.bpCode,
              endStationType: allLocations.find(el => el.key === props.formik.values.endStationId)?.type,
              endTimezoneId: props?.formik?.values?.endTimeZoneId
            }}
            updateDatasourceAcriss={updateDatasourceAcriss}
            submitCount={props.formik.submitCount}
            setPage={setPage}
            setInitialFormikValue={setInitialFormikValue}
            startStation={locations}
            reason={context.reason}
            tenant={tenant}
            statusNumber={statusNumber}
          />
        </>
      ) : (
        <>
          <TransferReasonPage subtitle={t("transferReason.subTitleHeader")} />
          <AddEditTransferReasonPage
            setPage={setPage}
            updateDatasourceAcriss={updateDatasourceAcriss}
            initialFormikValue={initialFormikValue}
            tenant={tenant}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default SearchModal;
